<template>
  <div ref="selectorContainer" class="selector-container flex items-center gap-0.5 rounded-lg bg-neutral-25 px-0.5">
    <div class="relative pl-1.5 py-2 pr-2 flex-shrink-0">
      <AdAccountIcon class="text-icon-normal transition-colors hover:text-icon-muted" 
      @mouseenter.native="showTooltip = true" @mouseleave.native="showTooltip = false" />
      <transition name="tooltip">
        <div v-if="showTooltip" class="ad-account-tooltip w-max">
          <BaseText type="label" size="xs" class="text-white px-1 py-0.5">
            {{ adAccountOptions?.length > 1 ? 'Displayed ad accounts' : 'Ad account' }}
          </BaseText>
        </div>
      </transition>
    </div>
    <!-- Selected Ad Account -->
    <div class="flex-shrink flex items-center gap-0.5 py-0.5 overflow-hidden min-w-0">
      <div v-for="account in visibleSelectedAccounts" :key="`filtered-account-${account.id}`"
      class="selected-ad-account flex items-center gap-1.5 p-1.5 rounded-md min-w-0">
        <component :is="getPlatformIcon(account.platform)" class="text-icon-normal flex-shrink-0" :width="16" :height="16" />
        <BaseText type="label" size="sm" class="text-text-muted truncate" style="max-width: 260px;">
          {{ account.name }}
        </BaseText>
      </div>
      <!-- Hidden Selection Indicator -->
      <div v-if="hiddenIndicator.show" class="selected-ad-account w-8 h-8 rounded-md flex items-center justify-center">
        <BaseText type="label" size="xs" class="text-text-muted">
          +{{ hiddenIndicator.count }}
        </BaseText>
      </div>
    </div>
    <!-- Dropdown Opener -->
    <button v-if="showDropdownOpener" class="w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-100 flex-shrink-0"
    :class="expanded ? 'bg-neutral-50' : 'hover:bg-neutral-50'"
    @click="expanded = !expanded">
      <ChevronIcon className="text-icon-normal transition-transform duration-100" :style="{ transform: expanded ? 'scaleY(-1)' : '' }" />
    </button>
    <!-- Ad Account Options Dropdown -->
    <transition>
      <div v-if="expanded" class="dropdown" :class="`${dropdownAlignOverride ?? dropdownAlign}-0`" 
      v-on-clickaway="() => { expanded = false }">
        <div class="dropdown-scroll w-full flex flex-col">
          <button v-for="account in adAccounts.connected" :key="`ad-account-option-${account.id}`"
          class="w-full flex items-center gap-2 px-1 py-1.5 rounded-md transition duration-100 min-w-0"
          :class="{'opacity-50': previewDeselect(account)}"
          @mouseenter="hoveredPlatform = account.platform" @mouseleave="hoveredPlatform = null"
          @click="handleOptionSelect(account)">
            <!-- Checkbox -->
            <div class="w-6 h-6 flex items-center justify-center cursor-pointer flex-shrink-0">
              <input type="checkbox" class="checkbox-input rounded text-black cursor-pointer focus:ring-transparent transition-colors pointer-events-none"
              :checked="account.isSelected && !previewDeselect(account)" :disabled="previewDeselect(account)" />
            </div>
            <component :is="account.icon" :width="16" :height="16" class="flex-shrink-0" />
            <BaseText type="label" size="sm" class="account-name text-text-muted truncate">
              {{ account.name }}
            </BaseText>
            <BaseText type="label" size="xs" class="text-text-muted px-1 py-0.5 rounded-md bg-neutral-25 flex-shrink-0 truncate" style="max-width: 150px;">
              {{ account.companyName }}
            </BaseText>
            <BaseText type="body" size="xs" class="account-id text-text-disabled">
              {{ account.id.replace('act_', '') }}
            </BaseText>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'

// Icons
import AdAccountIcon from '../globals/Icons/AdAccountIcon.vue'
import ChevronIcon from '../globals/Icons/ChevronIcon.vue'
import MetaPlatformIcon from '../globals/Icons/FilterIcons/Platforms/MetaPlatformIcon.vue'
import TikTokPlatformIcon from '../globals/Icons/FilterIcons/Platforms/TikTokPlatformIcon.vue'

export default {
  name: 'AdAccountFilterSelector',
  mixins: [clickaway],
  components: {
    AdAccountIcon,
    ChevronIcon
  },
  props: {
    adAccountOptions: {
      type: Array,
      required: true
    },
    selectedAccountIds: {
      type: Array,
      required: true
    },
    // If true, render all the selected ad accounts in the list area (that can fit)
    // To use effectively, ensure the parent element of this component has a width equal to the available grow area.
    renderMaxSelections: {
      type: Boolean,
      default: false
    },
    dropdownAlignOverride: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      expanded: false,
      hoveredPlatform: null,
      visibleSelectedAccounts: [],
      dropdownAlign: 'left',
      resizeObserver: null,
      showTooltip: false
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.setVisibleSelections()
      if (this.renderMaxSelections) {
        this.resizeObserver = new ResizeObserver(() => {
          requestAnimationFrame(() => {
            this.setVisibleSelections()
          })
        })
        this.resizeObserver.observe(this.$refs.selectorContainer.parentElement)
      }
    })
  },
  beforeDestroy () {
    if (this.resizeObserver) {
      if (this.$refs.selectorContainer?.parentElement) {
        this.resizeObserver.unobserve(this.$refs.selectorContainer.parentElement)
      }
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  },
  watch: {
    'adAccounts.selected': {
      handler (newSelectedAccounts) {
        this.$nextTick(() => {
          this.setVisibleSelections(newSelectedAccounts)
        })
      }
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricsPlatform']),
    adAccounts () {
      // Get the selected ad account objects while maintaining the order of selection
      // Order the connected ad accounts by platform
      let connectedByPlatform = {}; 
      let selected = Array.from({ length: this.selectedAccountIds.length }, () => ({}));
      for (const account of this.adAccountOptions) {
        const platform = account.platform
        const selectionIndex = this.selectedAccountIds.indexOf(account.id)
        const isSelected = selectionIndex !== -1
        connectedByPlatform[platform] = [
          ...(connectedByPlatform[platform] ?? []), 
          { ...account, isSelected, icon: this.getPlatformIcon(platform) }
        ]
        if (isSelected) selected[selectionIndex] = account
      }
      return { 
        connected: Object.values(connectedByPlatform)?.flat() ?? [], 
        selected 
      }
    },
    showDropdownOpener () {
      return this.adAccounts.connected.length > 1
    },
    hiddenIndicator () {
      return {
        show: this.adAccounts.selected.length > this.visibleSelectedAccounts.length,
        count: this.adAccounts.selected.length - this.visibleSelectedAccounts.length
      }
    }
  },
  methods: {
    handleOptionSelect (account) {
      if (this.getMetricsPlatform !== account.platform) {
        this.$emit('selection-change', {
          accountIds: [account.id],
          platform: account.platform
        })
      } else {
        const currentAccountIds = this.selectedAccountIds
        const newAccountIds = currentAccountIds.includes(account.id) 
          ? currentAccountIds.filter(id => id !== account.id) 
          : [...currentAccountIds, account.id]
        if (!newAccountIds.length) {
          this.$showAlert({
            type: 'info',
            message: 'At least one ad account must be selected'
          }) 
          return
        }
        this.$emit('selection-change', {
          accountIds: newAccountIds,
          platform: this.getMetricsPlatform
        })
      }
    },
    getPlatformIcon (platform) {
      return platform === 'meta' ? MetaPlatformIcon : TikTokPlatformIcon
    },
    previewDeselect (account) {
      if (!this.hoveredPlatform) return false
      return this.getMetricsPlatform === account.platform && this.hoveredPlatform !== account.platform
    },
    setVisibleSelections (selectedAccounts = null) {
      const selected = selectedAccounts ?? this.adAccounts.selected
      this.visibleSelectedAccounts = this.renderMaxSelections
        ? this.computeVisibleSelectedAccounts(selected)
        : [selected[0]]
    },
    computeVisibleSelectedAccounts (selectedAccounts = null) {
      const selected = selectedAccounts ?? this.adAccounts.selected
      const rootElm = this.$refs.selectorContainer
      if (!rootElm) return
      // Check the width of the component's parent to determine how much space is available for the selections list
      const availableWidth = rootElm.parentElement.getBoundingClientRect().width - 80 // -80px for static elements

      // Compute how many selected ad accounts can fit in the list area
      let totalWidth = 0
      let visibleSelectedAccounts = []
      for (const account of selected) {
        const compNode = document.createElement('span')
        compNode.classList.add('px-1.5', 'truncate', 'text-label-sm', 'opacity-0', 'pointer-events-none')
        compNode.style.maxWidth = '260px'
        compNode.textContent = account.name
        document.body.appendChild(compNode)
        const accountWidth = compNode.getBoundingClientRect().width + 22 // +22px for platform icon and gap
        document.body.removeChild(compNode)

        if (totalWidth + accountWidth + 2 < availableWidth) {
          totalWidth += accountWidth + 2 // +2px for gap
          visibleSelectedAccounts.push(account)
        } else {
          break
        }
      }

      // Determine the dropdown alignment
      this.dropdownAlign = totalWidth + 80 <= 428 ? 'left' : 'right'

      // Always show at least one selection
      return visibleSelectedAccounts.length ? visibleSelectedAccounts : [selected[0]]
    }
  }
}
</script>

<style scoped>
.selector-container {
  width: max-content;
  max-width: 100%;
  position: relative;
}
.selected-ad-account {
  background-color: white;
  box-shadow: 0 36px 48px -12px rgba(53, 57, 75, 0.20), 0 24px 32px -12px rgba(53, 57, 75, 0.24), 0 1px 2px 0 rgba(162, 172, 186, 0.16);
}
.dropdown {
  position: absolute;
  width: 428px;
  top: calc(100% + 4px);
  border-radius: 10px;
  box-shadow: 0 24px 32px -12px rgba(53, 57, 75, 0.12), 0 1px 2px 0 rgba(162, 172, 186, 0.08), 0 0 0 1px rgba(0, 56, 108, 0.04), 0 1px 2px 0 rgba(162, 172, 186, 0.16), 0 0 0 1px #EBEFF3;
  background-color: white;
  z-index: 500;
  overflow: hidden;
}
.dropdown-scroll {
  overflow-y: auto;
  max-height: 328px;
  padding: 2px 4px;
}
.dropdown-scroll::-webkit-scrollbar {
  width: 3px;
}
.dropdown-scroll::-webkit-scrollbar-thumb {
  background-color: #DFE1E7;
  border-radius: 18px;
}
.account-name {
  /* If we decide to make it wrap once before truncating: */
  /* display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical; */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.account-id {
  margin-left: auto;
  direction: rtl;
  flex: 0 999 auto;
  min-width: 6ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.checkbox-input {
  border: 1px;
  box-shadow: 0px 0px 0px 1px rgba(9, 25, 72, 0.13), 0px 1px 2px 0px rgba(9, 25, 72, 0.13);
  outline: none;
  -webkit-tap-highlight-color: transparent;
}
.checkbox-input:focus,
.checkbox-input:focus-visible,
.checkbox-input:active {
  outline: none;
  box-shadow: 0px 0px 0px 1px rgba(9, 25, 72, 0.13), 0px 1px 2px 0px rgba(9, 25, 72, 0.13);
}
.checkbox-input:disabled, .checkbox-input:disabled:hover {
  box-shadow: 0px 0px 0px 1px rgba(9, 25, 72, 0.06), 0px 1px 2px 0px rgba(9, 25, 72, 0.06);
  background-color: #ECEFF3;
}

.ad-account-tooltip {
  position: absolute;
  bottom: calc(100% + 2px);
  left: -4px;
  border-radius: 8px;
  background: rgba(6, 7, 16, 0.88);
  backdrop-filter: blur(6px);
  padding: 2px;
  z-index: 1000;
  pointer-events: none;
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>